import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '@catch/general-ui';
import { ScreenService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBars, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformAuthService } from '../../services/platform-auth.service';
import { PlatformSidenavService } from '../../services/platform-sidenav.service';
import { PlatformViewHeaderService } from '../../services/platform-view-header.service';

@Component({
  selector: 'app-platform-view-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, RouterLink, FaIconComponent, ButtonComponent],
  template: `
    <div class="view-header-inner">
      @if (isSmallScreen()) {
        <app-button
          variant="ghost"
          [iconStart]="icons.bars"
          [ariaLabel]="'jobboard.platform.sidenav.open-menu' | transloco"
          (click)="openSidenav()"
        />
      }
      <div class="view-header-content">
        <h3>{{ headingKey() | transloco: headingParams() }}</h3>
        <ul class="view-header-list">
          @for (breadcrumb of visibleBreadcrumbs(); track breadcrumb.label) {
            <li>
              <a
                [routerLink]="breadcrumb.routerLink"
                [queryParams]="breadcrumb.queryParams"
                [class.clickable]="!!breadcrumb.routerLink && breadcrumb.routerLink !== ''"
                >{{ breadcrumb.label | transloco }}</a
              >
              @if ($index < visibleBreadcrumbs().length - 1) {
                <fa-icon [icon]="icons.chevronRight" size="sm"></fa-icon>
              }
            </li>
          }
        </ul>
      </div>
    </div>
  `,
  styleUrl: './platform-view-header.component.scss',
})
export class PlatformViewHeaderComponent {
  private readonly screenService = inject(ScreenService);
  private readonly sidenavService = inject(PlatformSidenavService);
  private readonly auth = inject(PlatformAuthService);
  readonly viewHeader = inject(PlatformViewHeaderService);

  readonly icons = {
    bars: faBars,
    chevronRight: faChevronRight,
  };

  readonly isSmallScreen = toSignal(this.screenService.isSmallScreen$, {
    initialValue: this.screenService.isSmallScreen,
  });

  readonly visibleBreadcrumbs = computed(() =>
    this.viewHeader.breadcrumbs().filter((breadcrumb) => breadcrumb.label.trim() !== '')
  );

  readonly lastBreadcrumb = computed(() => this.viewHeader.breadcrumbs().at(-1));

  readonly headingKey = computed(
    () => this.viewHeader.title() ?? this.lastBreadcrumb()?.label ?? ''
  );

  readonly headingParams = computed(() => ({
    name: this.auth.displayUser().firstName,
  }));

  /**
   * Opens the sidenav on mobile.
   */
  openSidenav(): void {
    this.sidenavService.openSidenav();
  }
}
